import { Box, Chip, MenuItem, Select, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useConfig } from "../../contexts/ConfigContext";
import { RelatedEntityItem, RelationshipsResponse } from "../../types";
import RelatedItemCard from "./RelatedItemCard";
import { CMButton } from "../common/CMButton";

interface RelatedItemsRingProps {
  relationships: RelationshipsResponse;
  onItemClick?: (entity: RelatedEntityItem, category: string) => void;
  containerWidth: number;
  containerHeight: number;
}

// Approximate dimensions needed for the floating card
const CARD_WIDTH = 100; // Width of RelatedItemCard
const CARD_HEIGHT = 100; // Height of RelatedItemCard

// Central card dimensions (estimate or from a shared constant if available)
const CENTRAL_CARD_WIDTH = 400;
const CENTRAL_CARD_HEIGHT = 400; // Estimated height
const MIN_CLEARANCE_FROM_CENTRAL = 80; // Larger padding around central card to inner ring cards
const INTER_RING_SPACING = 50; // Larger spacing between edge of inner ring cards and edge of outer ring cards

const MAX_ITEMS_INNER_RING = 15; // Max items for the inner (direct) ring
const MAX_ITEMS_OUTER_RING = 30; // Max items for the outer (indirect) ring

// Target scale of available space for ring placement (0 to 1)
const INNER_RING_TARGET_SCALE = 0.8; // Inner ring uses up to 80% of available radius (increased from 70%)
const OUTER_RING_TARGET_SCALE = 0.9; // Outer ring uses up to 95% of available radius (increased from 90%)

interface ParsedRelDescription {
  relationship_type?: 'direct' | 'indirect' | 'default_to_direct';
  // Add other fields from the JSON if needed by the UI in the future
}

const parseRelDescription = (desc: string | undefined): ParsedRelDescription => {
  if (!desc) return { relationship_type: 'default_to_direct' }; // Missing description defaults to direct
  try {
    const parsed = JSON.parse(desc);
    if (parsed.relationship_type === 'indirect') {
      return { relationship_type: 'indirect' };
    }
    // Any other relationship_type value, or if it's missing from JSON, defaults to direct.
    // This includes explicit "direct" or any other string.
    return { relationship_type: 'default_to_direct' };
  } catch (e) {
    // If JSON.parse fails (e.g., it's plain text), it also defaults to direct.
    return { relationship_type: 'default_to_direct' };
  }
};

// Calculate position on an ellipse with given radii
const calculateEllipsePosition = (
  index: number,
  total: number,
  targetRadiusX: number,
  targetRadiusY: number
) => {
  if (total === 0) return { x: 0, y: 0 }; // Should not happen if items exist
  const angle = (index / total) * 2 * Math.PI + Math.PI / 2; // Add offset to start at top
  const x = targetRadiusX * Math.cos(angle);
  const y = targetRadiusY * Math.sin(angle);
  return {
    x: Math.round(x),
    y: Math.round(y),
  };
};

// PERFORMANCE: Custom comparison function for React.memo
const arePropsEqual = (prevProps: RelatedItemsRingProps, nextProps: RelatedItemsRingProps): boolean => {
  const propsEqual = (
    prevProps.relationships === nextProps.relationships &&
    prevProps.onItemClick === nextProps.onItemClick &&
    prevProps.containerWidth === nextProps.containerWidth &&
    prevProps.containerHeight === nextProps.containerHeight
  );
  
  console.log(`🔍 [RelatedItemsRing] MEMO: Props comparison result: ${propsEqual ? 'EQUAL (skip render)' : 'DIFFERENT (will render)'}`, {
    relationshipsEqual: prevProps.relationships === nextProps.relationships,
    onItemClickEqual: prevProps.onItemClick === nextProps.onItemClick,
    containerWidthEqual: prevProps.containerWidth === nextProps.containerWidth,
    containerHeightEqual: prevProps.containerHeight === nextProps.containerHeight,
    prevContainerDimensions: [prevProps.containerWidth, prevProps.containerHeight],
    nextContainerDimensions: [nextProps.containerWidth, nextProps.containerHeight]
  });
  
  return propsEqual;
};

const RelatedItemsRing: React.FC<RelatedItemsRingProps> = React.memo(({
  relationships,
  onItemClick,
  containerWidth,
  containerHeight,
}) => {
  const config = useConfig();
  const showMediaInRing = config.showMediaInRelatedItemsCloud || false;
  const showGeoInRing = config.showGeoInRelatedItemsCloud ?? false; // Read the geo config
  const showIndirectRelationshipsInRingFromConfig = config.showIndirectRelationshipsInRing ?? false;

  const returnedRelationshipsKeyToName = Object.fromEntries(config.apiEndpoints.map(it => [it.returnedRelationshipsKey, it.name]));

  // page indexing that starts at 1 not 0
  const [page, setPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("All");

  // PERFORMANCE MONITORING: Track expensive re-renders
  const renderCountRef = useRef(0);
  const lastPropsRef = useRef<any>({ relationships, containerWidth, containerHeight });
  
  useEffect(() => {
    renderCountRef.current++;
    const currentProps = { relationships, containerWidth, containerHeight };
    const prevProps = lastPropsRef.current;
    
    const changedProps = [];
    if (prevProps.relationships !== relationships) changedProps.push('relationships');
    if (prevProps.containerWidth !== containerWidth) changedProps.push('containerWidth');
    if (prevProps.containerHeight !== containerHeight) changedProps.push('containerHeight');
    
    if (changedProps.length > 0) {
      console.log(`🔍 [RelatedItemsRing] PERF: Render #${renderCountRef.current}, props changed:`, {
        renderCount: renderCountRef.current,
        changedProps: changedProps,
        containerWidth: containerWidth,
        containerHeight: containerHeight,
        relationshipKeys: relationships ? Object.keys(relationships) : [],
        relationshipIdentity: relationships === prevProps.relationships ? 'SAME' : 'DIFFERENT'
      });
    }
    
    lastPropsRef.current = currentProps;
  });

  // 🔍 DEBUG: Log RelatedItemsRing props and processing
  console.log('[RelatedItemsRing] 🔍 DEBUGGING RelatedItemsRing received:', {
    relationships,
    containerWidth,
    containerHeight,
    showMediaInRing,
    showGeoInRing,
    showIndirectRelationshipsInRingFromConfig,
    relationshipsKeys: relationships ? Object.keys(relationships) : null
  });

  if (!relationships) {
    console.log('[RelatedItemsRing] 🚫 No relationships - returning null');
    return null;
  }

  const allFlattenedItems = Object.entries(relationships).reduce<
    Array<{
      entity: RelatedEntityItem;
      category: string;
      entityType: 'geo' | 'related' | 'media';
    }>
  >((acc, [topLevelCategory, data]) => {
    // Handle regular related_entities (always include if present)
    if (topLevelCategory === "related_entities") {
      Object.entries(data as Record<string, RelatedEntityItem[]>).forEach(
        ([subCategory, items]) => {
          if (Array.isArray(items)) {
            acc.push(
              ...items.map((item) => ({ 
                entity: item, 
                category: subCategory,
                entityType: 'related' as const
              }))
            );
          }
        }
      );
    }
    // Handle geo_entities (include only if showGeoInRing is true)
    else if (topLevelCategory === "geo_entities" && showGeoInRing) {
      Object.entries(data as Record<string, RelatedEntityItem[]>).forEach(
        ([subCategory, items]) => {
          if (Array.isArray(items)) {
            acc.push(
              ...items.map((item) => ({ 
                entity: item, 
                category: subCategory,
                entityType: 'geo' as const
              }))
            );
          }
        }
      );
    }
    // Handle media (include only if showMediaInRing is true)
    else if (topLevelCategory === "media" && Array.isArray(data) && showMediaInRing) {
      acc.push(
        ...data.map((item) => ({
          entity: item,
          category: "media",
          entityType: 'media' as const
        }))
      );
    }
    return acc;
  }, []);

  // CRITICAL: Track data changes that might lose names
  const itemsWithoutNames = allFlattenedItems.filter(item => !item.entity.entity_name && !item.entity.name);
  
  if (itemsWithoutNames.length > 0) {
    console.error(`🚨 [RELATED-ITEMS-RING] DATA UPDATE LOST NAMES:`, {
      totalItems: allFlattenedItems.length,
      itemsWithoutNames: itemsWithoutNames.length,
      lostItems: itemsWithoutNames.map(item => ({
        id: item.entity.id,
        category: item.category,
        allKeys: Object.keys(item.entity)
      })),
      timestamp: new Date().toISOString()
    });
  }

  const directItems: Array<{ entity: RelatedEntityItem; category: string; entityType: 'geo' | 'related' | 'media'; }> = [];
  const indirectItems: Array<{ entity: RelatedEntityItem; category: string; entityType: 'geo' | 'related' | 'media'; }> = [];

  allFlattenedItems.forEach(item => {
    const parsedDesc = parseRelDescription(item.entity.rel_description);
    
    // An item is indirect ONLY if explicitly marked as such.
    // All other cases (default_to_direct, which includes plain text, missing desc, or explicit "direct") go to directItems.
    if (parsedDesc.relationship_type === 'indirect') {
      indirectItems.push(item);
    } else { 
      directItems.push(item);
    }
  });

  const filteredDirectItems = directItems.filter(it => selectedCategory === "All" || it.category === selectedCategory);
  const directDisplayItems = filteredDirectItems.slice((page - 1) * MAX_ITEMS_INNER_RING, page * MAX_ITEMS_INNER_RING);
  const indirectDisplayItems = indirectItems.slice(0, MAX_ITEMS_OUTER_RING);

  const numPages = Math.ceil(filteredDirectItems.length / MAX_ITEMS_INNER_RING);

  const directRelatedCategories = Array.from(new Set(directItems.map(it => it.category))).sort();

  // 🔍 DEBUG: Log the final processing results
  console.log('[RelatedItemsRing] 🔍 Final processing results:', {
    directItemsCount: directItems.length,
    indirectItemsCount: indirectItems.length,
    directDisplayItemsCount: directDisplayItems.length,
    indirectDisplayItemsCount: indirectDisplayItems.length,
    directDisplayItems: directDisplayItems.map(item => ({ category: item.category, entityType: item.entityType, name: item.entity.entity_name })),
    indirectDisplayItems: indirectDisplayItems.map(item => ({ category: item.category, entityType: item.entityType, name: item.entity.entity_name }))
  });

  // Calculate radii for the rings (distance from center of container to center of cards)
  const halfCardWidth = CARD_WIDTH / 2;
  const halfCardHeight = CARD_HEIGHT / 2;

  const containerSize = Math.min(containerWidth, containerHeight) - 100;

  // Max distance card centers can be from container center without card edges overflowing
  const maxDistXFromContainerCenter = Math.max(0, containerSize / 2 - halfCardWidth);
  const maxDistYFromContainerCenter = Math.max(0, containerSize / 2 - halfCardHeight);

  // Inner Ring (Direct Items)
  const minRequiredInnerRadiusX = CENTRAL_CARD_WIDTH / 2 + MIN_CLEARANCE_FROM_CENTRAL + halfCardWidth;
  const minRequiredInnerRadiusY = CENTRAL_CARD_HEIGHT / 2 + MIN_CLEARANCE_FROM_CENTRAL + halfCardHeight;
  
  const desiredInnerRadiusX = maxDistXFromContainerCenter * INNER_RING_TARGET_SCALE;
  const desiredInnerRadiusY = maxDistYFromContainerCenter * INNER_RING_TARGET_SCALE;

  let actualInnerRadiusX = Math.min(maxDistXFromContainerCenter, Math.max(minRequiredInnerRadiusX, desiredInnerRadiusX));
  let actualInnerRadiusY = Math.min(maxDistYFromContainerCenter, Math.max(minRequiredInnerRadiusY, desiredInnerRadiusY));
  
  // Ensure non-negative values if calculations somehow result in them (e.g., very small container)
  actualInnerRadiusX = Math.max(0, actualInnerRadiusX);
  actualInnerRadiusY = Math.max(0, actualInnerRadiusY);

  // Outer Ring (Indirect Items)
  // Minimum radius for outer ring cards to clear inner ring cards
  const minRequiredOuterRadiusX = actualInnerRadiusX + halfCardWidth + INTER_RING_SPACING + halfCardWidth;
  const minRequiredOuterRadiusY = actualInnerRadiusY + halfCardHeight + INTER_RING_SPACING + halfCardHeight;

  const desiredOuterRadiusX = maxDistXFromContainerCenter * OUTER_RING_TARGET_SCALE;
  const desiredOuterRadiusY = maxDistYFromContainerCenter * OUTER_RING_TARGET_SCALE;

  let actualOuterRadiusX = Math.min(maxDistXFromContainerCenter, Math.max(minRequiredOuterRadiusX, desiredOuterRadiusX));
  let actualOuterRadiusY = Math.min(maxDistYFromContainerCenter, Math.max(minRequiredOuterRadiusY, desiredOuterRadiusY));

  actualOuterRadiusX = Math.max(0, actualOuterRadiusX);
  actualOuterRadiusY = Math.max(0, actualOuterRadiusY);

  const canDisplayInnerRing = directDisplayItems.length > 0;
  const canDisplayPagination = numPages >= 2;

  // If outer ring is not meaningfully larger than inner, or no items, don't render it or adjust
  const canDisplayOuterRing = 
    showIndirectRelationshipsInRingFromConfig &&
    indirectDisplayItems.length > 0 &&
    (actualOuterRadiusX > actualInnerRadiusX + CARD_WIDTH || actualOuterRadiusY > actualInnerRadiusY + CARD_HEIGHT);


  return (
    <>
      <Box
        sx={{
          top: "75px",
          position: "absolute",
          textAlign: "center",
          width: "350px",
          // border: '1px dashed lightblue', // For debugging layout
        }}
      >
       {        directRelatedCategories.length === 1 ? <Typography
          variant="caption"
          sx={{
            position: "absolute",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
            color: "rgba(255, 255, 255, 0.6)", // Subtle, muted color
            fontSize: "0.7rem",
            textAlign: "center",
            pointerEvents: "none", // Don't interfere with interactions
            backgroundColor: "rgba(0, 0, 0, 0.3)", // Subtle background
            padding: "2px 8px",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >{directRelatedCategories[0]} ({directDisplayItems.length} items)</Typography> :
        directRelatedCategories.length >= 2 ? <CMButton
          variant="outlined"
          sx={{
            flexDirection: "column",
            pointerEvents: "all"
          }}
        >
          <Box sx={{display: "block", width: "100%"}}>Displaying</Box>
          <Select
            value={selectedCategory}
            label={selectedCategory}
            onChange={(e) => {
              const selected = e.target.value as string;
              setSelectedCategory(selected);
            }}
            sx={{
              color: "white",
              height: "20px"
            }}
          >
            {["All"].concat(directRelatedCategories).map(category => (
              <MenuItem
                key={category}
                value={category}
                sx={{
                  pointerEvents: "all"
                }}
              >{returnedRelationshipsKeyToName[category] || category.toUpperCase()}</MenuItem>
            ))}
          </Select>
        </CMButton> :
        <CMButton
          variant="outlined"
          sx={{
            pointerEvents: "all"
          }}
        >No Directly Related Items</CMButton>}
      </Box>
      <Box
        sx={{
          position: "absolute", // Relative to its positioned parent
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          //border: '1px dashed lightblue', // For debugging layout
        }}
      >
        {/* Direct Items - Inner Ring */}
        {directDisplayItems.length > 0 && actualInnerRadiusX > 0 && actualInnerRadiusY > 0 && directDisplayItems.map(({ entity, category, entityType }, index) => {
          const position = calculateEllipsePosition(
            index,
            directDisplayItems.length,
            actualInnerRadiusX,
            actualInnerRadiusY
          );
          return (
            <RelatedItemCard
              key={`direct-${category}-${entity.id}-${index}`}
              entity={entity}
              category={category}
              position={position}
              onClick={() => onItemClick?.(entity, category)}
              isDirect={true}
              entityType={entityType}
            />
          );
        })}

        {/* Indirect Items - Outer Ring */}
        {canDisplayOuterRing && actualOuterRadiusX > 0 && actualOuterRadiusY > 0 && indirectDisplayItems.map(({ entity, category, entityType }, index) => {
          const position = calculateEllipsePosition(
            index,
            indirectDisplayItems.length,
            actualOuterRadiusX,
            actualOuterRadiusY
          );
          return (
            <RelatedItemCard
              key={`indirect-${category}-${entity.id}-${index}`}
              entity={entity}
              category={category}
              position={position}
              onClick={() => onItemClick?.(entity, category)}
              isDirect={false}
              entityType={entityType}
            />
          );
        })}
      </Box>

      {canDisplayPagination &&
        <Box
          sx={{
            bottom: "75px",
            position: "absolute",
            width: "400px",
            // border: '1px dashed lightblue', // For debugging layout
          }}
        >
          <CMButton
            onClick={() => {
              if (page === 1) {
                setPage(numPages);
              } else {
                setPage(page - 1);
              }
            }}
            sx={{
              pointerEvents: "all"
            }}
            variant="outlined"
          >
            Back
          </CMButton>
          <CMButton
            variant="outlined"
            sx={{
              left: "50%",
              position: "absolute",
              transform: "translateX(-50%)"
            }}
          >
            {canDisplayInnerRing ? <>Showing Page {page} of {numPages}</> : <>No Related Media Found</>}
          </CMButton>            
          <CMButton
            onClick={() => {
              if (page === numPages) {
                setPage(1);
              } else {
                setPage(page + 1);
              }
            }}
            variant="outlined"
            sx={{
              pointerEvents: "all",
              position: "absolute",
              right: "0px"
            }}
          >
            Next
          </CMButton>        
        </Box>
      }
    </>
  );
}, arePropsEqual); // Use custom comparison function

RelatedItemsRing.displayName = 'RelatedItemsRing';

export default RelatedItemsRing;